<template>
  <div class="itserve-center">
    <loader :show="showLoader" :text="loaderText" />
    <div class="card">
      <div class="card-header">
        <h2>{{ $t("NoComissionPay.paymentWithoutCommission") }}</h2>
      </div>
      <div class="card-body" v-if="error">
        <div class="error-message">{{ error }}</div>
      </div>
      <div class="card-body" v-else>
        <div class="account-info">
          <div class="info-row">
            <div class="label">{{ $t("NoComissionPay.accountNumber") }}</div>
            <div class="value">{{ accountNumber }}</div>
          </div>
          <div class="info-row">
            <div class="label">
              <span v-if="displayBalance >= 0">
                {{ $t("NoComissionPay.debt") }}
              </span>
              <span v-else>{{ $t("NoComissionPay.overPay") }}</span>
            </div>
            <div
              class="value"
              :class="{
                'positive-balance': displayBalance > 0,
                'negative-balance': displayBalance < 0,
                'zero-balance': displayBalance === 0,
                'loading-balance': !isBalanceLoaded,
              }"
            >
              {{ Math.abs(displayBalance) }} {{ $t("NoComissionPay.uah") }}
            </div>
          </div>
        </div>

        <!-- Блок оценки приложения (показывается после успешной оплаты) -->
        <div
          v-if="paymentResponse && paymentResponse.success"
          class="rating-prompt"
        >
          <div class="success-message" style="margin-bottom: 20px">
            {{ $t("NoComissionPay.paymentSuccessMessage") }}
          </div>
          <p class="rating-text">
            {{ $t("NoComissionPay.rateAppPrompt") }}
          </p>
          <div class="rating-stars">★★★★★</div>
          <itserve-button
            @click="rateApp"
            class="itserve-button-wide"
            :value="$t('NoComissionPay.rateAppButton')"
            btnStyle="primary"
          />
        </div>

        <!-- Кнопка закрыть (показывается после успешной оплаты) -->
        <div
          v-if="paymentResponse && paymentResponse.success"
          class="actions"
          style="margin-top: 15px"
        >
          <itserve-button
            @click="resetPaymentResponse"
            class="btn-secondary"
            :value="$t('NoComissionPay.closeButton')"
            btnStyle="secondary"
          />
        </div>

        <div
          class="payment-form"
          v-if="!paymentResponse || !paymentResponse.success"
        >
          <div class="form-group">
            <label for="cardNumber">{{
              $t("NoComissionPay.cardNumber")
            }}</label>
            <input
              type="text"
              id="cardNumber"
              v-model="paymentData.cardNumber"
              placeholder="XXXX XXXX XXXX XXXX"
              maxlength="19"
              @input="formatCardNumber"
            />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="cardExpiry">{{
                $t("NoComissionPay.cardExpiry")
              }}</label>
              <input
                type="text"
                id="cardExpiry"
                v-model="paymentData.cardExpiry"
                placeholder="--/--"
                maxlength="5"
                @input="formatExpiryDate"
              />
            </div>
            <div class="form-group">
              <label for="cardCvv">{{ $t("NoComissionPay.cardCvv") }}</label>
              <input
                type="password"
                id="cardCvv"
                v-model="paymentData.cardCvv"
                placeholder="XXX"
                maxlength="3"
                @input="validationError = null"
              />
            </div>
          </div>
          <div class="form-group">
            <label for="amount">{{ $t("NoComissionPay.amount") }}</label>
            <input
              type="number"
              id="amount"
              v-model="paymentData.amount"
              :placeholder="$t('NoComissionPay.enterAmount')"
              @input="validationError = null"
            />
          </div>

          <!-- Отображаем ошибку валидации перед кнопкой оплаты -->
          <div class="error-message" v-if="validationError">
            {{ validationError }}
          </div>

          <!-- Отображаем сообщение об ошибке от сервера -->
          <div
            v-if="paymentResponse && !paymentResponse.success"
            class="error-message"
          >
            {{ paymentResponse.message || $t("NoComissionPay.checkDebt") }}
            <div class="actions" style="margin-top: 15px">
              <itserve-button
                @click="resetPaymentResponse"
                class="btn-secondary"
                :value="$t('NoComissionPay.tryAgainButton')"
                btnStyle="secondary"
              />
            </div>
          </div>

          <!-- Чекбокс условий -->
          <div class="terms-checkbox" v-if="!paymentResponse">
            <label class="terms-label">
              <input
                type="checkbox"
                v-model="termsAccepted"
                class="terms-input"
              />
              <span class="terms-text">
                {{ $t("NoComissionPay.termsAgreement") }}
                <router-link
                  :to="{ name: 'TermsPage' }"
                  class="terms-link"
                  @click.native.stop
                >
                  {{ $t("NoComissionPay.termsAgreementLink") }}
                </router-link>
              </span>
            </label>
          </div>

          <!-- Отображаем список сохраненных карт -->
          <div
            v-if="cards.length && (!paymentResponse || !paymentResponse.success)"
            class="saved-cards-section"
          >
            <h3>{{ $t("NoComissionPay.chooseCard") }}</h3>
            <div class="saved-cards-list">
              <div
                v-for="card in cards"
                :key="card.id"
                class="saved-card-item"
                @click="chooseCard(card.id)"
              >
                <div class="card-icon">
                  <img
                    :src="
                      card.masked.startsWith('4')
                        ? require('@/assets/images/visa.png')
                        : require('@/assets/images/mastercard.png')
                    "
                    class="card-type-image"
                  />
                </div>
                <div class="card-number">{{ card.masked }}</div>
              </div>
            </div>
          </div>

          <div class="actions" v-if="!paymentResponse">
            <itserve-button
              @click="pay"
              :disabled="false"
              class="btn-pay"
              :value="$t('NoComissionPay.pay')"
              btnStyle="primary"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from "@/components/content/Loader";
import ItserveButton from "@/components/forms/ItserveButton";

const STRIPE_SCRIPT_ID = "stripe-js-v3";
const PAYMENT_FLOW_SESSION_KEY = "paymentFlowInProgress";
const PAYMENT_FLOW_TTL_MS = 15 * 60 * 1000;
const STRIPE_PUBLISHABLE_KEY =
  "pk_live_51TEAwxGaksIhzRJklGNt1hauRrYJFrejxwmRUnCG0087mCoot0NwuurdfmbZTB46L4ROo5qNe4acdnyFZD4gev3R00VRBvdsNe";
const STRIPE_3DS_CANCELED_MESSAGE =
  "Підтвердження в банку скасовано або не завершено. Спробуйте ще раз і підтвердіть дію в застосунку банку.";

export default {
  name: "NoComissionPay",
  components: {
    Loader,
    ItserveButton,
  },
  data() {
    return {
      accountNumber: this.$route.params.account.ls,
      showLoader: false,
      isDataLoading: true, // Флаг загрузки данных
      loaderText: "", // Текст для лоадера
      error: null,
      validationError: null,
      termsAccepted: true,
      paymentResponse: null,
      stripe: null,
      stripePublishableKey: null,
      paymentData: {
        cardNumber: "",
        cardExpiry: "",
        cardCvv: "",
        amount: 100,
      },
    };
  },
  computed: {
    debtData() {
      const storeAccount = this.$store.state.Auth.account;
      if (storeAccount && storeAccount.balance !== undefined) {
        return storeAccount.balance;
      }
      return this.$route.params.account
        ? this.$route.params.account.balance
        : undefined;
    },
    storePaymentResponse() {
      return this.$store.state.KhmelnytskenergozbutTariffmax.paymentResponse;
    },
    cards() {
      return this.$store.state.KhmelnytskenergozbutTariffmax.cards || [];
    },
    isBalanceLoaded() {
      return this.debtData !== undefined && this.debtData !== null;
    },
    displayBalance() {
      return this.isBalanceLoaded ? this.debtData : 100;
    },
  },
  watch: {
    debtData(newVal) {
      if (newVal !== undefined && newVal !== null) {
        if (newVal > 0) {
          this.paymentData.amount = Math.abs(newVal);
        } else if (newVal === 0) {
          this.paymentData.amount = 10;
        }
      }
    },
  },
  async created() {
    console.log("CHLENIX NO COMISSION: ", JSON.stringify(this.$route.params));
    // this.showLoader = true; // Показываем лоадер при загрузке
    // this.loaderText = this.$t("NoComissionPay.loadingDebtInfo"); // Используем новый текст для загрузки данных о задолженности
    try {
      // Получаем данные о задолженности
      // await this.$store.dispatch(
      //   "KhmelnytskenergozbutTariffmax/checkDebt",
      //   this.accountNumber
      // );

      // Получаем список сохраненных карт

      await this.$store.dispatch("KhmelnytskenergozbutTariffmax/getCards");

      // Устанавливаем сумму долга в поле оплаты, если есть данные о долге
      if (this.debtData !== undefined) {
        if (this.debtData > 0) {
          this.paymentData.amount = Math.abs(this.debtData);
        } else if (this.debtData === 0) {
          // Установим небольшую сумму по умолчанию при нулевом долге
          this.paymentData.amount = 10;
        }
      }
    } catch (error) {
      console.error("Error checking debt:", error);
      this.error = "Ошибка при проверке задолженности";
    } finally {
      this.showLoader = false; // Скрываем лоадер после загрузки
      this.isDataLoading = false; // Разрешаем отображение контента после загрузки данных
    }
  },
  methods: {
    markPaymentFlowInProgress() {
      const value = String(Date.now());
      sessionStorage.setItem(PAYMENT_FLOW_SESSION_KEY, value);
      localStorage.setItem(PAYMENT_FLOW_SESSION_KEY, value);
      localStorage.setItem(
        `${PAYMENT_FLOW_SESSION_KEY}ExpiresAt`,
        String(Date.now() + PAYMENT_FLOW_TTL_MS)
      );
      localStorage.removeItem("show_accounts_on_next_launch");
    },
    clearPaymentFlowInProgress() {
      sessionStorage.removeItem(PAYMENT_FLOW_SESSION_KEY);
      localStorage.removeItem(PAYMENT_FLOW_SESSION_KEY);
      localStorage.removeItem(`${PAYMENT_FLOW_SESSION_KEY}ExpiresAt`);
    },
    normalizeStripeResponse(raw) {
      if (!raw) return raw;
      return raw.data || raw.response?.data || raw;
    },
    findStripe3dsPayload(raw) {
      const data = this.normalizeStripeResponse(raw);
      if (!data || typeof data !== "object") return null;

      if (data.payment_state === "stripe_3ds_required") {
        return data;
      }

      const candidates = [data.data, data.response, data.result].filter(
        Boolean
      );
      for (const candidate of candidates) {
        const found = this.findStripe3dsPayload(candidate);
        if (found) return found;
      }

      return null;
    },
    loadStripeScript() {
      if (window.Stripe) {
        return Promise.resolve();
      }

      const existingScript = document.getElementById(STRIPE_SCRIPT_ID);
      if (existingScript) {
        return new Promise((resolve, reject) => {
          existingScript.addEventListener("load", resolve, { once: true });
          existingScript.addEventListener("error", reject, { once: true });
        });
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.id = STRIPE_SCRIPT_ID;
        script.src = "https://js.stripe.com/v3/";
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async getStripe(publishableKey) {
      await this.loadStripeScript();

      if (!this.stripe || this.stripePublishableKey !== publishableKey) {
        this.stripe = window.Stripe(publishableKey);
        this.stripePublishableKey = publishableKey;
      }

      return this.stripe;
    },
    async handleStripe3ds(data) {
      const clientSecret = data.client_secret;
      const publishableKey =
        data.publishable_key ||
        process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY ||
        STRIPE_PUBLISHABLE_KEY;

      if (!clientSecret || !publishableKey) {
        throw new Error(this.$t("NoComissionPay.paymentErrorMessage"));
      }

      const stripe = await this.getStripe(publishableKey);
      const isSetupIntent =
        data.intent_type === "setup" || clientSecret.startsWith("seti_");
      const result = isSetupIntent
        ? await stripe.confirmCardSetup(clientSecret)
        : await stripe.confirmCardPayment(clientSecret);

      if (result.error) {
        console.log("3DS confirmation error", {
          code: result.error.code,
          message: result.error.message,
          intentType: data.intent_type,
          clientSecretPrefix: clientSecret.slice(0, 12),
          keyPrefix: publishableKey.slice(0, 16),
        });
        throw new Error(STRIPE_3DS_CANCELED_MESSAGE);
      }

      return result;
    },
    async repeatAfterStripe3dsIfRequired(response, retryFn) {
      const data = this.findStripe3dsPayload(response);
      if (!data) return response;

      await this.handleStripe3ds(data);
      return await retryFn();
    },
    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async refreshSavedCardsAfterPayment(previousCardsCount = 0) {
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          await this.$store.dispatch("KhmelnytskenergozbutTariffmax/getCards");
          if (this.cards.length > previousCardsCount || attempt === 4) {
            return;
          }

          await this.wait(700);
        } catch (error) {
          console.error("Refresh saved cards error:", error);
          if (attempt < 4) {
            await this.wait(700);
          }
        }
      }
    },
    isSuccessfulPayment(response) {
      return Boolean(
        response &&
          (response.status === "success" ||
            response.success === true ||
            response.payment_state === "paid")
      );
    },
    formatCardNumber() {
      // Сбрасываем ошибку валидации при изменении поля
      this.validationError = null;

      // Удаляем все нецифровые символы
      let value = this.paymentData.cardNumber.replace(/\D/g, "");

      // Добавляем пробелы после каждых 4 цифр
      let formattedValue = "";
      for (let i = 0; i < value.length; i++) {
        if (i > 0 && i % 4 === 0) {
          formattedValue += " ";
        }
        formattedValue += value[i];
      }

      this.paymentData.cardNumber = formattedValue;
    },

    formatExpiryDate() {
      // Сбрасываем ошибку валидации при изменении поля
      this.validationError = null;

      // Удаляем все нецифровые символы, кроме /
      let value = this.paymentData.cardExpiry.replace(/[^0-9/]/g, "");

      // Удаляем лишние символы /
      if (
        value.indexOf("/") !== -1 &&
        value.indexOf("/") !== value.lastIndexOf("/")
      ) {
        value = value.substring(0, value.lastIndexOf("/"));
      }

      // Форматируем в виде MM/YY
      if (value.length > 0) {
        if (value.length < 3) {
          // Если введено меньше 3 символов, просто оставляем цифры
          value = value.replace(/\D/g, "");
          // Проверяем, что месяц не больше 12
          if (value.length === 2) {
            const month = parseInt(value);
            if (month > 12) {
              value = "12";
            } else if (month === 0) {
              value = "01";
            }
          }
        } else {
          // Если больше 2 символов, форматируем как MM/YY
          const month = value.substring(0, 2);
          let year = value.substring(2).replace(/\D/g, "");

          // Проверяем месяц
          const monthNum = parseInt(month);
          if (monthNum > 12) {
            value = "12/" + year;
          } else if (monthNum === 0) {
            value = "01/" + year;
          } else {
            value = month + "/" + year;
          }
        }
      }

      // Добавляем / после ввода 2 цифр месяца
      if (value.length === 2 && !value.includes("/")) {
        value += "/";
      }

      this.paymentData.cardExpiry = value;
    },

    validateForm() {
      if (!this.termsAccepted) {
        this.validationError = this.$t("NoComissionPay.errorTermsNotAccepted");
        return false;
      }

      if (
        !this.paymentData.cardNumber ||
        this.paymentData.cardNumber.replace(/\s/g, "").length < 16
      ) {
        this.validationError = this.$t("NoComissionPay.errorCardNumber");
        return false;
      }

      if (
        !this.paymentData.cardExpiry ||
        this.paymentData.cardExpiry.length < 5
      ) {
        this.validationError = this.$t("NoComissionPay.errorCardExpiry");
        return false;
      }

      if (!this.paymentData.cardCvv || this.paymentData.cardCvv.length < 3) {
        this.validationError = this.$t("NoComissionPay.errorCardCvv");
        return false;
      }

      if (!this.paymentData.amount || this.paymentData.amount <= 0) {
        this.validationError = this.$t("NoComissionPay.errorAmount");
        return false;
      }

      this.validationError = null;
      return true;
    },

    rateApp() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;

      // Определение iOS
      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        this.nativeSendEvent(
          "url",
          "https://apps.apple.com/ua/app/volyn-energo/id6445990114"
        );
        return;
      }

      // Определение Android
      if (/android/i.test(userAgent)) {
        this.nativeSendEvent(
          "url",
          "https://play.google.com/store/apps/details?id=volyn.energo&hl=uk"
        );
        return;
      }

      // Запасной вариант для десктопа и других ОС
      this.nativeSendEvent(
        "url",
        "https://play.google.com/store/apps/details?id=volyn.energo&hl=uk"
      );
    },

    resetPaymentResponse() {
      this.clearPaymentFlowInProgress();
      // Если оплата успешная, возвращаемся на страницу аккаунта
      if (this.paymentResponse && this.paymentResponse.success) {
        // this.$router.push({
        //   name: "Account",
        //   params: { account: this.accountNumber },
        // });
        this.$router.push({ path: "/home" });
      } else {
        // Если оплата неуспешная, сбрасываем данные карты и ответ сервера
        this.paymentData.cardNumber = "";
        this.paymentData.cardExpiry = "";
        this.paymentData.cardCvv = "";
        this.paymentResponse = null;
      }
    },

    // Метод для выбора сохраненной карты и оплаты
    async chooseCard(cardId) {
      if (!this.termsAccepted) {
        this.validationError = this.$t("NoComissionPay.errorTermsNotAccepted");
        return;
      }
      this.loaderText = this.$t("NoComissionPay.loadingText");
      this.showLoader = true;
      this.validationError = null;
      this.error = null;
      this.paymentResponse = null;

      try {
        // Проверяем, что сумма указана
        if (!this.paymentData.amount || this.paymentData.amount <= 0) {
          this.validationError = this.$t("NoComissionPay.errorAmount");
          this.showLoader = false;
          return;
        }

        const paymentDetails = {
          ls: this.accountNumber,
          amount: this.paymentData.amount,
          cardId: cardId,
        };

        this.markPaymentFlowInProgress();

        let response = await this.$store.dispatch(
          "KhmelnytskenergozbutTariffmax/payWithSavedCard",
          paymentDetails
        );
        console.log(
          "Payment with saved card response: ",
          JSON.stringify(response)
        );

        response = await this.repeatAfterStripe3dsIfRequired(response, () =>
          this.$store.dispatch(
            "KhmelnytskenergozbutTariffmax/payWithSavedCard",
            paymentDetails
          )
        );

        if (this.isSuccessfulPayment(response)) {
          // Показываем сообщение об успешной оплате
          this.paymentResponse = {
            success: true,
            message:
              response.message ||
              this.$t("NoComissionPay.paymentSuccessMessage"),
          };
        } else {
          // Показываем сообщение об ошибке
          this.paymentResponse = {
            success: false,
            message:
              response?.message ||
              this.$t("NoComissionPay.paymentErrorMessage"),
          };
        }
      } catch (error) {
        console.error("Payment with saved card error:", error);
        this.paymentResponse = {
          success: false,
          message: error.message || this.$t("NoComissionPay.paymentErrorMessage"),
        };
      } finally {
        this.showLoader = false; // Скрываем лоадер после завершения оплаты
      }
    },

    async pay() {
      if (!this.validateForm()) {
        return;
      }

      this.loaderText = this.$t("NoComissionPay.loadingText"); // Устанавливаем текст для лоадера
      this.showLoader = true; // Показываем только лоадер во время оплаты
      this.validationError = null;
      this.error = null;
      this.paymentResponse = null;
      const previousCardsCount = this.cards.length;

      try {
        // Удаляем пробелы из номера карты перед отправкой на бэкенд
        const cleanCardNumber = this.paymentData.cardNumber.replace(/\s/g, "");

        const paymentDetails = {
          ls: this.accountNumber,
          amount: this.paymentData.amount,
          cardNumber: cleanCardNumber,
          cardExpiry: this.paymentData.cardExpiry,
          cardCvv: this.paymentData.cardCvv,
        };

        this.markPaymentFlowInProgress();

        let response = await this.$store.dispatch(
          "KhmelnytskenergozbutTariffmax/makePayment",
          paymentDetails
        );
        console.log("Payment response: ", JSON.stringify(response));

        response = await this.repeatAfterStripe3dsIfRequired(response, () =>
          this.$store.dispatch(
            "KhmelnytskenergozbutTariffmax/makePayment",
            paymentDetails
          )
        );

        if (this.isSuccessfulPayment(response)) {
          await this.refreshSavedCardsAfterPayment(previousCardsCount);
          // Показываем сообщение об успешной оплате
          this.paymentResponse = {
            success: true,
            message:
              response.message ||
              this.$t("NoComissionPay.paymentSuccessMessage"),
          };
        } else {
          await this.refreshSavedCardsAfterPayment(previousCardsCount);
          // Показываем сообщение об ошибке
          this.paymentResponse = {
            success: false,
            message:
              response?.message ||
              this.$t("NoComissionPay.paymentErrorMessage"),
          };
        }
      } catch (error) {
        console.error("Payment error:", error);
        await this.refreshSavedCardsAfterPayment(previousCardsCount);
        this.paymentResponse = {
          success: false,
          message: error.message || this.$t("NoComissionPay.paymentErrorMessage"),
        };
      } finally {
        this.showLoader = false; // Скрываем лоадер после завершения оплаты
      }
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.account-info {
  text-align: left;
}

.no-comission-pay {
  height: 100%;
  background-color: #000;
  color: #fff;
}

.container {
  max-width: 100%;
  margin: 0 auto;
  height: 100%;
}

.card {
  background: #0e121b;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.card-header {
  color: #fff;
  padding: 15px 20px;
  text-align: center;
  border-bottom: 1px solid #333;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
}

.card-body {
  padding: 20px;
  flex: 1;
}

.loader {
  text-align: center;
  padding: 30px 0;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.loader:after {
  content: "";
  width: 40px;
  height: 40px;
  border: 4px solid #4477f6;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-message {
  color: #f87c7c;
  margin: 15px 0;
  padding: 10px;
  background-color: rgba(248, 124, 124, 0.2);
  border-radius: 4px;
  text-align: center;
  font-weight: bold;
  border-left: 4px solid #f87c7c;
}

.success-message {
  color: #4caf50;
  margin: 15px 0;
  padding: 10px;
}

.rating-prompt {
  margin-top: 20px;
  padding: 20px;
  background-color: rgba(66, 119, 245, 0.1);
  border-radius: 8px;
  text-align: center;
}

.rating-prompt .success-message {
  background-color: rgba(76, 175, 80, 0.2);
  border-left: 4px solid #4caf50;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
}

.rating-text {
  color: #ecf0f1;
  font-size: 16px;
  margin-bottom: 10px;
}

.rating-stars {
  font-size: 28px;
  color: #ffd700;
  margin: 15px 0;
  letter-spacing: 5px;
  text-align: center;
  font-weight: bold;
}

.info-row {
  display: flex;
  margin-bottom: 10px;
  padding-bottom: 10px;
}

.label {
  flex: 1;
  color: #fff;
}

.value {
  flex: 1;
  text-align: right;
  color: #fff;
  font-weight: bold;
}

.payment-form {
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.payment-form h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 18px;
  color: #fff;
  text-align: center;
}

.form-group {
  margin-bottom: 20px;
  width: 100%;
  box-sizing: border-box;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  width: 100%;
}

.form-row .form-group {
  flex: 1;
  padding: 0;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #fff;
  text-align: left;
}

.form-group.account-info {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #333;
  width: 100%;
}

.form-group input {
  width: 100%;
  padding: 12px;
  border: 1px solid #333;
  border-radius: 4px;
  background-color: #222;
  color: #fff;
  margin-top: 5px;
  box-sizing: border-box;
}

.actions {
  margin-top: 30px;
  width: 100%;
  box-sizing: border-box;
}

button {
  width: 100%;
  padding: 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.3s;
}

/* Стили для кнопки ItserveButton */
::v-deep .btn-pay {
  width: 100%;
  margin-top: 10px;
}

::v-deep .btn-pay button {
  background-color: #4477f6 !important;
  color: #fff !important;
  border: none !important;
  font-size: 16px !important;
  width: 100% !important;
  height: 50px !important;
  border-radius: 4px !important;
}

::v-deep .btn-pay button:hover {
  background-color: #3366e5 !important;
}

::v-deep .btn-pay button:disabled {
  background-color: #555 !important;
  cursor: not-allowed !important;
  opacity: 0.7 !important;
}

::v-deep .btn-secondary button {
  background-color: #555 !important;
  color: #fff !important;
  border: none !important;
  font-size: 16px !important;
  width: 100% !important;
  height: 40px !important;
  border-radius: 4px !important;
}

::v-deep .btn-secondary button:hover {
  background-color: #444 !important;
}

.no-debt {
  text-align: center;
  padding: 20px 0;
  color: #fff;
}

.positive-balance {
  color: #f87c7c !important;
}

.negative-balance {
  color: #4caf50 !important;
}

.zero-balance {
  color: #2ecc71 !important;
}

.loading-balance {
  opacity: 0.5;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

/* Контейнер чекбокса */
.terms-checkbox {
  margin: 15px 0;
  padding: 12px;
  background-color: rgba(68, 119, 246, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(68, 119, 246, 0.3);
}

/* Строка: чекбокс + текст */
.terms-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  text-align: left;
}

/* Сам input[type=checkbox] */
.terms-input {
  width: 20px;
  height: 20px;
  min-width: 20px;
  margin-top: 2px;
  accent-color: #4477f6;
  cursor: pointer;
}

/* Текст рядом с чекбоксом */
.terms-text {
  color: #b0b0b0;
  font-size: 13px;
  line-height: 1.4;
}

/* Ссылка "Умов обслуговування" */
.terms-link {
  color: #4477f6;
  text-decoration: underline;
  cursor: pointer;
}

/* Стили для списка сохраненных карт */
.saved-cards-section {
  margin: 20px 0;
  width: 100%;
}

.saved-cards-section h3 {
  font-size: 16px;
  margin-bottom: 15px;
  color: #fff;
  text-align: center;
}

.saved-cards-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.saved-card-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  background-color: #222;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.saved-card-item:hover {
  background-color: #333;
}

.card-icon {
  margin-right: 15px;
}

.card-type-image {
  width: 40px;
  height: auto;
}

.card-number {
  font-size: 16px;
  color: #fff;
}
</style>
